import { ExpenseGroup } from "../../types/daily-expense.type";
import {
  ExpenseCategoryName,
  MonthlyExpenseBranchRow,
  MonthlyExpenseGroup,
} from "../../types/monthly-expense.type";
import { formatNumber } from "../../utils";
import { GROUP_LABEL } from "../DailyExpense/dailyExpenseForm";
import { COLUMN_GROUP_KEYS, ColumnGroupKey, MINUS_SIGN } from "./constants";
import { dailyCellKeyOf } from "./monthlyExpenseDraft";

/** One column of the S4 table, in screen order. */
export type CloseColumn =
  | { kind: "DAILY"; groupKey: "DAILY"; categoryCode: string; group: ExpenseGroup; label: string }
  | { kind: "ITEM"; groupKey: MonthlyExpenseGroup; categoryCode: string; label: string }
  | { kind: "EXTRA"; groupKey: MonthlyExpenseGroup }
  | { kind: "GROUP_TOTAL"; groupKey: ColumnGroupKey };

/** The columns each group has when unfolded (its รวม column is always there). */
export type CloseLayout = Record<ColumnGroupKey, CloseColumn[]>;

/**
 * The columns come from the rows (the server lists every active category, plus any old one that
 * still holds an amount), named from the category list; "อื่นๆ" gets its group in the name.
 */
export const closeLayoutOf = (
  rows: MonthlyExpenseBranchRow[],
  categories: ExpenseCategoryName[],
): CloseLayout => {
  const byCode = new Map(categories.map((category) => [category.code, category]));
  const nameOf = (code: string) => byCode.get(code)?.name ?? code;
  const daily = new Map<string, CloseColumn>();
  const items = new Map<string, CloseColumn>();
  rows.forEach((row) => {
    row.daily.forEach((cell) => {
      const key = dailyCellKeyOf(cell.category_code, cell.expense_group);
      const isOther = byCode.get(cell.category_code)?.expense_group === null;
      const name = nameOf(cell.category_code);
      if (!daily.has(key)) {
        daily.set(key, {
          kind: "DAILY",
          groupKey: "DAILY",
          categoryCode: cell.category_code,
          group: cell.expense_group,
          label: isOther ? `${name} ${GROUP_LABEL[cell.expense_group]}` : name,
        });
      }
    });
    row.items.forEach((item) => {
      if (!items.has(item.category_code)) {
        items.set(item.category_code, {
          kind: "ITEM",
          groupKey: item.expense_group,
          categoryCode: item.category_code,
          label: nameOf(item.category_code),
        });
      }
    });
  });
  const itemColumns = Array.from(items.values());
  const groupOf = (groupKey: MonthlyExpenseGroup): CloseColumn[] => [
    ...itemColumns.filter((column) => column.groupKey === groupKey),
    { kind: "EXTRA", groupKey },
  ];
  return {
    DAILY: Array.from(daily.values()),
    SALES: groupOf("SALES"),
    SALARY: groupOf("SALARY"),
    ADMIN: groupOf("ADMIN"),
  };
};

/** The columns on screen: an unfolded group shows its columns, every group its รวม. */
export const visibleColumnsOf = (
  layout: CloseLayout,
  expanded: Record<ColumnGroupKey, boolean>,
): CloseColumn[] =>
  COLUMN_GROUP_KEYS.flatMap((groupKey) => [
    ...(expanded[groupKey] ? layout[groupKey] : []),
    { kind: "GROUP_TOTAL", groupKey },
  ]);

/** +0.39 / −0.39 — a sign always, as the design rule asks for a change. */
export const formatSignedAmount = (value: number): string => {
  if (value > 0) return `+${formatNumber(value)}`;
  if (value < 0) return `${MINUS_SIGN}${formatNumber(Math.abs(value))}`;
  return formatNumber(0);
};

/** A stable React key for a column. */
export const columnKeyOf = (column: CloseColumn): string => {
  if (column.kind === "DAILY") return `DAILY-${dailyCellKeyOf(column.categoryCode, column.group)}`;
  if (column.kind === "ITEM") return `ITEM-${column.categoryCode}`;
  return `${column.kind}-${column.groupKey}`;
};

/** The first column of a group carries the group's left border. */
export const isGroupStart = (columns: CloseColumn[], index: number): boolean =>
  index === 0 || columns[index - 1].groupKey !== columns[index].groupKey;
